import { NgModule } from '@angular/core'
import { NoPreloading, PreloadAllModules, RouterModule, Routes } from '@angular/router'
import { Capacitor } from '@capacitor/core'
import { AuthGuardService } from './core/auth-guard.service'
import { Role } from './models/role'
import { AppPage } from './site/app/app.page'
import { FEATURES } from './shared/feature-flags'

const routes: Routes = [
  { path: '', redirectTo: 'landing', pathMatch: 'full' },
  { path: 'landing', loadChildren: () => import('./landing/landing.module').then((m) => m.LandingPageModule) },
  {
    path: 'app',
    canActivate: [AuthGuardService],
    component: AppPage,
    data: { roles: [Role.Manufacturer, Role.Contractor, Role.Owner, Role.Admin] },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadChildren: () => import('./site/app/dashboard/dashboard.module').then((m) => m.DashboardPageModule),
      },
      {
        path: 'compliance-dashboard',
        loadChildren: () => import('./site/app/compliance-dashboard/compliance-dashboard.module').then((m) => m.ComplianceDashboardPageModule),
      },
      // Public sign-off is switched off until public report submission ships (shared/feature-flags.ts).
      ...(FEATURES.publicSignoff
        ? [
            {
              path: 'public-report-signoff',
              canActivate: [AuthGuardService],
              data: { roles: [Role.SignOff] },
              loadChildren: () => import('./site/app/public-report-signoff/public-report-signoff.module').then((m) => m.PublicReportSignoffPageModule),
            },
          ]
        : [{ path: 'public-report-signoff', redirectTo: 'dashboard', pathMatch: 'full' as const }]),
      {
        path: 'firedoor-elevations',
        canActivate: [AuthGuardService],
        data: { roles: [Role.Elevations] },
        loadChildren: () => import('./site/app/firedoor-elevations/firedoor-elevations.module').then((m) => m.FiredoorElevationsPageModule),
      },
      {
        path: 'products',
        loadChildren: () => import('./site/app/products/products.module').then((m) => m.ProductsPageModule),
      },
      {
        path: 'customise-data',
        loadChildren: () => import('./site/app/customise-data/customise-data.module').then((m) => m.CustomiseDataPageModule),
      },
      {
        path: 'customise-data-v2',
        loadChildren: () => import('./site/app/customise-data-v2/customise-data-v2.module').then((m) => m.CustomiseDataV2PageModule),
      },
      {
        path: 'admin',
        canActivate: [AuthGuardService],
        data: { roles: [Role.Admin], profileType: 'Admin' },
        loadChildren: () => import('./site/app/admin/admin.module').then((m) => m.AdminPageModule),
      },
      {
        path: 'collections',
        loadChildren: () => import('./site/app/collections/collections.module').then((m) => m.CollectionsPageModule),
      },
      // Job schedules (the worklist feature, task-framed). Old /worklists links keep working.
      { path: 'worklists', redirectTo: 'job-schedules', pathMatch: 'full' },
      { path: 'worklists/:id', redirectTo: 'job-schedules/:id' },
      {
        path: 'job-schedules',
        canActivate: [AuthGuardService],
        // Manage role, or anyone whose company has Job schedules on (they see what's shared with them)
        data: { roles: [Role.JobSchedules], orProfileFlag: 'jobSchedulesEnabled' },
        loadChildren: () => import('./site/app/worklists/worklists.module').then((m) => m.WorklistsPageModule),
      },
      // Smart views are switched off while Job schedules ship on their own (shared/feature-flags.ts).
      ...(FEATURES.smartViews
        ? [
            {
              path: 'smart-views',
              loadChildren: () => import('./site/app/smart-views/smart-views.module').then((m) => m.SmartViewsPageModule),
            },
          ]
        : [{ path: 'smart-views', redirectTo: 'job-schedules', pathMatch: 'prefix' as const }]),
      {
        path: 'users',
        canActivate: [AuthGuardService],
        data: { roles: [Role.UserAdmin, Role.Admin] },
        loadChildren: () => import('./site/app/users/users.module').then((m) => m.UsersPageModule),
      },
      // {
      //   path: 'sign-off',
      //   canActivate: [AuthGuardService],
      //   data: { roles: [Role.SignOff] },
      //   loadChildren: () => import('./site/app/sign-off/sign-off.module').then(m => m.SignOffPageModule)
      // },
      {
        path: 'import-subcollection',
        canActivate: [AuthGuardService],
        data: { roles: [Role.Import] },
        loadChildren: () => import('./site/app/import-subcollection/import-subcollection.module').then((m) => m.ImportSubcollectionPageModule),
      },
      {
        path: 'audits',
        canActivate: [AuthGuardService],
        data: { roles: [Role.Audit] },
        loadChildren: () => import('./site/app/audits/audits.module').then((m) => m.AuditsPageModule),
      },
      {
        path: 'inspection',
        canActivate: [AuthGuardService],
        data: { roles: [Role.Inspection] },
        loadChildren: () => import('./site/app/inspection/inspection.module').then((m) => m.InspectionPageModule),
      },
      {
        path: 'collection-manager',
        loadChildren: () => import('./site/app/collection-manager/collection-manager.module').then((m) => m.CollectionManagerPageModule),
      },
      {
        path: 'generated-reports',
        loadChildren: () => import('./site/app/generated-reports/generated-reports.module').then((m) => m.GeneratedReportsPageModule),
      },
      {
        path: 'importer',
        loadChildren: () => import('./site/app/importer/importer.module').then((m) => m.ImporterPageModule),
      },
      {
        path: 'custom-products',
        loadChildren: () => import('./site/app/custom-products/custom-products.module').then((m) => m.CustomProductsPageModule),
      },
      {
        path: 'custom-reporting',
        loadChildren: () => import('./site/app/custom-reporting/custom-reporting.module').then((m) => m.CustomReportingPageModule),
      },
      {
        path: 'company-profile-details',
        loadChildren: () => import('./site/app/company-profile-details/company-profile-details.module').then((m) => m.CompanyProfileDetailsPageModule),
      },
      // Report information and job schedule settings live on the profile page, opened from Settings
      {
        path: 'settings/report-information',
        data: { section: 'report' },
        loadChildren: () => import('./site/app/company-profile-details/company-profile-details.module').then((m) => m.CompanyProfileDetailsPageModule),
      },
      {
        path: 'settings/job-schedules',
        data: { section: 'job-schedules' },
        loadChildren: () => import('./site/app/company-profile-details/company-profile-details.module').then((m) => m.CompanyProfileDetailsPageModule),
      },
      {
        path: 'settings',
        loadChildren: () => import('./site/app/settings/settings.module').then((m) => m.SettingsPageModule),
      },
      {
        path: 'reporting',
        loadChildren: () => import('./site/app/reporting/reporting.module').then((m) => m.ReportingPageModule),
      },
    ],
  },
  {
    canActivate: [AuthGuardService],
    data: { roles: [Role.UserAdmin, Role.Admin] },
    path: 'app/floorplate-editor',
    loadChildren: () => import('./modals/floorplate-editor/floorplate-editor.module').then((m) => m.FloorplateEditorPageModule),
  },
  {
    path: 'nfc-select-product',
    loadChildren: () => import('./modals/nfc-select-product/nfc-select-product.module').then((m) => m.NfcSelectProductPageModule),
  },
  {
    path: 'app',
    loadChildren: () => import('./site/app/app.module').then((m) => m.AppPageModule),
  },
  {
    path: 'transfer-product',
    loadChildren: () => import('./modals/transfer-product/transfer-product.module').then((m) => m.TransferProductPageModule),
  },
  {
    path: 'public',
    loadChildren: () => import('./public/public.module').then((m) => m.PublicPageModule),
  },
  {
    path: 'product-changelog',
    loadChildren: () => import('./modals/product-changelog/product-changelog.module').then((m) => m.ProductChangelogPageModule),
  },
  {
    path: 'floorplate-media-manager',
    loadChildren: () => import('./modals/floorplate-media-manager/floorplate-media-manager.module').then((m) => m.FloorplateMediaManagerPageModule),
  },
  {
    path: 'elevation-builder',
    loadChildren: () =>
      import('./modals/custom-product-modal-elevation-builder/custom-product-modal-elevation-builder.module').then(
        (m) => m.CustomProductModalElevationBuilderModule
      ),
  },
  {
    path: 'transactionlog',
    loadChildren: () => import('./modals/transactionlog/transactionlog.module').then((m) => m.TransactionlogPageModule),
  },
  {
    path: 'console-log',
    loadChildren: () => import('./modals/console-log/console-log.module').then((m) => m.ConsoleLogPageModule),
  },
  {
    path: 'profile-overview',
    loadChildren: () => import('./modals/profile-overview/profile-overview.module').then((m) => m.ProfileOverviewPageModule),
  },
  {
    path: 'accept-eula',
    loadChildren: () => import('./modals/accept-eula/accept-eula.module').then((m) => m.AcceptEulaPageModule),
  },
  {
    path: 'document-create',
    loadChildren: () => import('./modals/document-create/document-create.module').then((m) => m.DocumentCreatePageModule),
  },
  {
    path: 'debug-view',
    loadChildren: () => import('./modals/debug-view/debug-view.module').then((m) => m.DebugViewPageModule),
  },
  {
    path: 'firedoor-importer',
    loadChildren: () => import('./components/import-manager/firedoor-importer/firedoor-importer.module').then((m) => m.FiredoorImporterPageModule),
  },
  {
    path: 'asset-detail-report',
    loadChildren: () => import('./modals/asset-detail-report/asset-detail-report.module').then((m) => m.AssetDetailReportPageModule),
  },
  {
    path: 'excel-report',
    loadChildren: () => import('./modals/excel-report/excel-report.module').then((m) => m.ExcelReportPageModule),
  },
]

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // Native bundles are local files, so preloading buys nothing but evaluates every heavy
      // chunk (docx, exceljs, fabric, syncfusion, maps) into the renderer heap right after login.
      // Web keeps the prefetch — the service worker caches the chunks anyway.
      preloadingStrategy: Capacitor.isNativePlatform() ? NoPreloading : PreloadAllModules,
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
